(() => {
  const SELECTOR = '[data-bgs-gallery]';

  class BegodeHistoryGallery {
    constructor(root) {
      if (root.dataset.bgsReady === 'true') return;
      root.dataset.bgsReady = 'true';
      this.root = root;
      this.viewport = root.querySelector('[data-bgs-viewport]');
      this.slides = [...root.querySelectorAll('[data-bgs-slide]')];
      this.previousButton = root.querySelector('[data-bgs-prev]');
      this.nextButton = root.querySelector('[data-bgs-next]');
      this.jumpButtons = [...root.querySelectorAll('[data-bgs-jump]')];
      this.currentLabel = root.querySelector('[data-bgs-current]');
      this.dialog = root.querySelector('[data-bgs-lightbox]');
      this.lightboxImage = root.querySelector('[data-bgs-lightbox-image]');
      this.lightboxCaption = root.querySelector('[data-bgs-lightbox-caption]');
      this.lightboxPrevious = root.querySelector('[data-bgs-lightbox-prev]');
      this.lightboxNext = root.querySelector('[data-bgs-lightbox-next]');
      this.closeButton = root.querySelector('[data-bgs-close]');
      this.index = 0;
      this.openingButton = null;
      this.scrollFrame = 0;
      this.touchStartX = null;
      this.touchStartIndex = 0;
      if (!this.viewport || !this.slides.length) return;
      this.bind();
      this.update();
    }

    bind() {
      this.previousButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));
      this.viewport.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          const slideWidth = this.viewport.clientWidth || 1;
          this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.viewport.scrollLeft / slideWidth)));
          this.scrollFrame = 0;
          this.update();
        });
      }, { passive: true });
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        }
        if (event.key === 'Home') {
          event.preventDefault();
          this.goTo(0);
        }
        if (event.key === 'End') {
          event.preventDefault();
          this.goTo(this.slides.length - 1);
        }
      });
      this.slides.forEach((slide, index) => {
        slide.querySelector('[data-bgs-open]')?.addEventListener('click', (event) => {
          this.openingButton = event.currentTarget;
          this.openLightbox(index);
        });
      });
      this.closeButton?.addEventListener('click', () => this.closeLightbox());
      this.lightboxPrevious?.addEventListener('click', () => this.openLightbox(this.index - 1));
      this.lightboxNext?.addEventListener('click', () => this.openLightbox(this.index + 1));
      this.jumpButtons.forEach((button) => button.addEventListener('click', () => this.goTo(Number(button.dataset.bgsJump))));
      this.viewport.addEventListener('touchstart', (event) => {
        this.touchStartX = event.touches[0]?.clientX ?? null;
        this.touchStartIndex = this.index;
      }, { passive: true });
      this.viewport.addEventListener('touchend', (event) => {
        if (this.touchStartX === null || this.slides.length < 2) return;
        const endX = event.changedTouches[0]?.clientX ?? this.touchStartX;
        const distance = this.touchStartX - endX;
        if (distance > 50 && this.touchStartIndex === this.slides.length - 1 && this.index === this.slides.length - 1) this.goTo(0);
        if (distance < -50 && this.touchStartIndex === 0 && this.index === 0) this.goTo(this.slides.length - 1);
        this.touchStartX = null;
      }, { passive: true });
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeLightbox();
      });
      this.dialog?.addEventListener('close', () => this.openingButton?.focus());
    }

    goTo(index) {
      this.index = this.normalizeIndex(index);
      this.ensureImage(this.index);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.viewport.scrollTo({ left: this.viewport.clientWidth * this.index, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.update();
    }

    normalizeIndex(index) {
      return (index % this.slides.length + this.slides.length) % this.slides.length;
    }

    ensureImage(index) {
      const image = this.slides[index]?.querySelector('img');
      if (!image?.dataset.bgsSrc) return image;
      image.src = image.dataset.bgsSrc;
      if (image.dataset.bgsSrcset) image.srcset = image.dataset.bgsSrcset;
      delete image.dataset.bgsSrc;
      delete image.dataset.bgsSrcset;
      return image;
    }

    update() {
      this.ensureImage(this.index);
      if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1);
      const onlyOne = this.slides.length < 2;
      if (this.previousButton) this.previousButton.disabled = onlyOne;
      if (this.nextButton) this.nextButton.disabled = onlyOne;
      if (this.lightboxPrevious) this.lightboxPrevious.disabled = onlyOne;
      if (this.lightboxNext) this.lightboxNext.disabled = onlyOne;
      this.jumpButtons.forEach((button, index) => {
        if (index === this.index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }

    openLightbox(index) {
      this.index = this.normalizeIndex(index);
      const slide = this.slides[this.index];
      const source = this.ensureImage(this.index);
      if (!source || !this.dialog || !this.lightboxImage) return;
      this.lightboxImage.src = source.dataset.bgsFullSrc || source.currentSrc || source.src;
      this.lightboxImage.alt = source.alt;
      if (this.lightboxCaption) this.lightboxCaption.textContent = slide.dataset.caption || '';
      this.update();
      if (!this.dialog.open) {
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      }
      this.closeButton?.focus();
    }

    closeLightbox() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
    }
  }

  const initialize = (scope = document) => scope.querySelectorAll(SELECTOR).forEach((gallery) => new BegodeHistoryGallery(gallery));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
